import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MessageCircle } from "lucide-react";
import { cn } from "../../lib/utils";
import { Container, Eyebrow } from "../ui/Primitives";
import { Button } from "../ui/Button";
import { useI18n } from "../../i18n";
import { CONTACT_WHATSAPP } from "../../lib/contact";
import { useDemo } from "../demo/DemoContext";

/* Reusable WhatsApp CTA ---------------------------------------------------- */
export function WhatsAppButton({ variant = "outline", size = "lg", className, label }) {
  const { t } = useI18n();
  return (
    <Button
      href={CONTACT_WHATSAPP}
      target="_blank"
      rel="noopener noreferrer"
      variant={variant}
      size={size}
      className={className}
    >
      <MessageCircle className="h-5 w-5" /> {label || t("contact.whatsapp.cta")}
    </Button>
  );
}

/* Inner-page hero with cyber-grid backdrop -------------------------------- */
export function PageHero({ eyebrow, title, lead, accent = "brand", children, breadcrumb }) {
  return (
    <section className="relative overflow-hidden border-b border-border bg-neutral-950 text-white">
      <div className="absolute inset-0 cyber-grid opacity-70" />
      <div
        className="pointer-events-none absolute -top-24 right-0 h-96 w-96 rounded-full blur-3xl"
        style={{ background: `hsl(var(--${accent}) / 0.18)` }}
      />
      <Container className="relative py-16 sm:py-20">
        {breadcrumb && (
          <nav className="mb-4 flex items-center gap-1.5 font-mono text-xs text-white/50">
            <Link to="/" className="hover:text-white">
              Home
            </Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-white/80">{breadcrumb}</span>
          </nav>
        )}
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h1 className="mt-4 max-w-3xl text-4xl leading-[1.1] text-white sm:text-5xl">{title}</h1>
        {lead && <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/70">{lead}</p>}
        {children && <div className="mt-8">{children}</div>}
      </Container>
    </section>
  );
}

/* Feature card ------------------------------------------------------------ */
export function FeatureCard({ icon: Icon, title, desc, accent = "brand" }) {
  return (
    <div className="group rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-md">
      <div
        className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl"
        style={{ background: `hsl(var(--${accent}) / 0.12)`, color: `hsl(var(--${accent}))` }}
      >
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-base font-bold">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{desc}</p>
    </div>
  );
}

/* Animated impact counter ------------------------------------------------- */
export function AnimatedCounter({ value, suffix = "", prefix = "", duration = 1600 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(Math.floor(eased * value));
            if (p < 1) requestAnimationFrame(tick);
            else setDisplay(value);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}

/* Closing CTA band -------------------------------------------------------- */
export function CTASection() {
  const { t } = useI18n();
  const { openDemo } = useDemo();
  return (
    <section className="relative overflow-hidden border-y border-border bg-neutral-950 py-16 text-white">
      <div className="absolute inset-0 cyber-grid opacity-60" />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-40"
        style={{ background: "radial-gradient(600px 200px at 50% 0%, hsl(var(--brand)/.25), transparent)" }}
      />
      <Container className="relative text-center">
        <h2 className="mx-auto max-w-2xl text-3xl sm:text-4xl">{t("home.cta.title")}</h2>
        <p className="mx-auto mt-4 max-w-xl text-white/70">{t("home.cta.subtitle")}</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button size="lg" onClick={openDemo}>
            {t("demo.cta")}
          </Button>
          <Button to="/contato" size="lg" variant="light">
            {t("common.bookDemo")}
          </Button>
        </div>
      </Container>
    </section>
  );
}

/* Small video-tile used across hero & mockups ----------------------------- */
export function VideoTile({ label, alert = false, accent = "brand", small = false }) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-lg border bg-neutral-900",
        alert ? "border-destructive/60" : "border-white/10"
      )}
    >
      <div
        className="aspect-video w-full"
        style={{
          background:
            "repeating-linear-gradient(135deg, rgba(255,255,255,.03) 0 8px, rgba(255,255,255,.06) 8px 16px)",
        }}
      />
      <div className="absolute left-1.5 top-1.5 flex items-center gap-1">
        <span
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            alert ? "bg-destructive animate-pulse" : "bg-amber-400"
          )}
        />
        {!small && <span className="font-mono text-[9px] text-white/70">{label}</span>}
      </div>
      {alert && (
        <span className="absolute right-1.5 top-1.5 rounded bg-destructive/90 px-1 font-mono text-[8px] font-bold text-white">
          !
        </span>
      )}
    </div>
  );
}
